// /mnt/mantenimientos/frontend/src/componentsTecnico/PcCard.jsx

import React, { useEffect, useMemo, useState } from "react";
import "./PcCard.css";

import {
  normalizeZabbixEntry,
  isPfSense,
  filterPfSenseInterfaces,
  summarizeInterface,
} from "../utils/zabbix";

function pct(v) {
  const n = Number(v);

  if (!Number.isFinite(n)) {
    return "—";
  }

  return `${n.toFixed(1)}%`;
}

function gb(v) {
  const n = Number(v);

  if (!Number.isFinite(n)) {
    return "—";
  }

  return `${n.toFixed(2)} GB`;
}

function fmtUptime(seconds) {
  const s = Number(seconds);

  if (!Number.isFinite(s) || s < 0) {
    return "—";
  }

  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);

  return `${days}d ${hours}h ${mins}m`;
}

function fmtClock(unix) {
  const t = Number(unix);

  if (!Number.isFinite(t) || t <= 0) {
    return "—";
  }

  try {
    return new Date(t * 1000).toLocaleString("es-ES");
  } catch {
    return String(t);
  }
}

function barWidthPct(usedPct) {
  const n = Number(usedPct);

  if (!Number.isFinite(n)) {
    return 0;
  }

  return Math.max(0, Math.min(100, n));
}

const MAINTENANCE_STATUS = {
  completed: { label: "Completado", icon: "✅" },
  partial: { label: "Parcial", icon: "🟡" },
  blocked: { label: "Bloqueado", icon: "⛔" },
  follow_up: { label: "Requiere seguimiento", icon: "📅" },
  not_completed: { label: "No realizado", icon: "❌" },
};

function maintenanceStatus(record) {
  const key = record?.status || (Number(record?.realizado) === 1 ? "completed" : "not_completed");
  return MAINTENANCE_STATUS[key] || MAINTENANCE_STATUS.not_completed;
}

function ramLabel(z) {
  const pctTxt = pct(z?.ram_pct);
  const total = Number(z?.ram_total_gb);

  if (!Number.isFinite(total)) {
    return pctTxt;
  }

  return `${pctTxt} (${gb(total)})`;
}

function serviceLabelFromValue(v) {
  const n = Number(v);

  const map = {
    0: "running",
    6: "stopped",
    7: "unknown",
    255: "not_found",
  };

  return map[n] || "unknown";
}

function servicePill(label) {
  const l = String(label || "").toLowerCase();

  if (l === "running") {
    return {
      cls: "badge badge-ok",
      icon: "✅",
      text: "OK",
    };
  }

  if (l === "stopped" || l === "not_found") {
    return {
      cls: "badge badge-bad",
      icon: "❌",
      text: "KO",
    };
  }

  return {
    cls: "badge badge-warn",
    icon: "⚠️",
    text: "WARN",
  };
}

/**
 * Abre una conexión de TeamViewer.
 */
function openTeamViewer(id) {
  const tvId = String(id || "").trim();

  if (!tvId) {
    return;
  }

  const deepLink = `teamviewer://remotecontrol?connectid=${encodeURIComponent(
    tvId
  )}`;

  const webLink = `https://start.teamviewer.com/${encodeURIComponent(tvId)}`;

  window.location.href = deepLink;

  setTimeout(() => {
    window.open(webLink, "_blank", "noopener,noreferrer");
  }, 700);
}

function getService(servicesArr, name) {
  if (!Array.isArray(servicesArr)) {
    return null;
  }

  const normalizedName = String(name || "").toLowerCase();

  return (
    servicesArr.find(
      (service) =>
        String(service?.name || "").toLowerCase() === normalizedName
    ) || null
  );
}

function getFirstService(servicesArr, names = []) {
  for (const name of names) {
    const service = getService(servicesArr, name);

    if (service) {
      return service;
    }
  }

  return null;
}

export default function PcCard({
  pc,
  ultimo,
  historial = [],

  form,
  onFormChange,
  onGuardar,
  onGuardarYSiguiente,

  zabbix,
  zabbixLoading = false,
  onRefreshZabbix,
  onUpdateInfo,
  focusMaintenanceId,
}) {
  const [showHistorial, setShowHistorial] = useState(false);
  const [showZabbixMore, setShowZabbixMore] = useState(false);
  const [activeView, setActiveView] = useState("work");
  const [showLastMaintenance, setShowLastMaintenance] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoDraft, setInfoDraft] = useState("");
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoError, setInfoError] = useState("");
  const [saveStatus, setSaveStatus] = useState({ state: "idle", message: "" });

  useEffect(() => {
    if (focusMaintenanceId && ultimo && String(focusMaintenanceId) === String(ultimo.id)) {
      setActiveView("work");
      setShowLastMaintenance(true);
    }
  }, [focusMaintenanceId, ultimo]);

  const changeForm = (field, value) => {
    setSaveStatus({ state: "idle", message: "" });
    onFormChange(field, value);
  };

  const saveMaintenance = async (andNext = false) => {
    if (saveStatus.state === "saving") return;
    setSaveStatus({ state: "saving", message: "Guardando mantenimiento…" });
    try {
      if (andNext) await onGuardarYSiguiente();
      else await onGuardar();
      setSaveStatus({ state: "success", message: andNext ? "Guardado. Abriendo el siguiente pendiente…" : "Mantenimiento guardado correctamente." });
    } catch (error) {
      setSaveStatus({ state: "error", message: error?.message || "No se pudo guardar el mantenimiento." });
    }
  };

  // Normalizamos Zabbix usando la utilidad común.
  const zNorm = useMemo(
    () => normalizeZabbixEntry(zabbix),
    [zabbix]
  );

  const zRoot = zNorm.root;
  const z = zNorm.metricas;

  const zHostName = useMemo(
    () => zNorm.host || "",
    [zNorm.host]
  );

  // Detectamos pfSense usando el objeto raíz.
  const pfSense = useMemo(
    () => isPfSense(zRoot),
    [zRoot]
  );

  const agentOk = useMemo(() => {
    if (!z) {
      return null;
    }

    const value = Number(z.agent_ping);

    if (!Number.isFinite(value)) {
      return null;
    }

    return value === 1;
  }, [z]);

  const disks = useMemo(() => {
    const arr = zNorm.disks;

    if (!Array.isArray(arr)) {
      return [];
    }

    return [...arr].sort((a, b) =>
      String(a.drive).localeCompare(String(b.drive))
    );
  }, [zNorm.disks]);

  const worstDisk = useMemo(() => {
    if (!disks.length) {
      return null;
    }

    let worst = null;

    for (const disk of disks) {
      const used = Number(disk.used_pct);

      if (!Number.isFinite(used)) {
        continue;
      }

      if (!worst || used > Number(worst.used_pct)) {
        worst = disk;
      }
    }

    return worst;
  }, [disks]);

  const problems = useMemo(
    () => zNorm.problems || [],
    [zNorm.problems]
  );

  const services = useMemo(
    () => zNorm.services || [],
    [zNorm.services]
  );

  const incidenciasCount = problems.length || 0;

  // Interfaces activas de pfSense.
  const pfIfaces = useMemo(() => {
    const list = zNorm.interfaces || [];

    return filterPfSenseInterfaces(list, {
      hide: ["enc0", "pflog0", "pfsync0", "lo0", "lo"],
      onlyActive: true,
    });
  }, [zNorm.interfaces]);

  const tvId = String(pc?.teamviewer_id || "").trim();
  const hasTv = Boolean(tvId);

  const infoAdicional = String(pc?.info_adicional || "").trim();

  const startEditingInfo = () => {
    setInfoDraft(pc?.info_adicional || "");
    setInfoError("");
    setEditingInfo(true);
  };

  const saveInfo = async () => {
    if (!onUpdateInfo) return;
    try {
      setSavingInfo(true);
      setInfoError("");
      await onUpdateInfo(infoDraft.trim());
      setEditingInfo(false);
    } catch (error) {
      setInfoError(error?.response?.data?.error || error?.message || "No se pudo guardar la información");
    } finally {
      setSavingInfo(false);
    }
  };

  const ultimoObs = String(
    ultimo?.observaciones || ""
  ).trim();

  const ultimoInc = String(
    ultimo?.incidencias || ""
  ).trim();
  const selectedStatus = form?.status || (String(form?.realizado ?? "1") === "1" ? "completed" : "not_completed");

  // Servicios considerados críticos.
  const criticalServices = useMemo(() => {
    if (!services.length) {
      return [];
    }

    const picks = [
      {
        title: "TeamViewer",
        names: ["TeamViewer"],
      },
      {
        title: "Antivirus (Defender)",
        names: ["WinDefend"],
      },
      {
        title: "Firewall",
        names: ["mpssvc", "BFE"],
      },
      {
        title: "Windows Update",
        names: ["wuauserv"],
      },
      {
        title: "Update Orchestrator",
        names: ["UsoSvc"],
      },
      {
        title: "Spooler",
        names: ["Spooler"],
      },
      {
        title: "SyncBack",
        names: ["SyncBackProSchedulesMonitor"],
      },
      {
        title: "DHCP Client",
        names: ["Dhcp"],
      },
      {
        title: "DNS Client",
        names: ["Dnscache"],
      },
      {
        title: "EventLog",
        names: ["EventLog"],
      },
      {
        title: "Zabbix Agent 2",
        names: ["Zabbix Agent 2"],
      },
    ];

    const result = [];

    for (const pick of picks) {
      const service = getFirstService(
        services,
        pick.names
      );

      if (!service) {
        continue;
      }

      const label =
        service.label && String(service.label).trim()
          ? service.label
          : serviceLabelFromValue(service.value);

      result.push({
        title: pick.title,
        name: service.name,
        value: service.value,
        label,
      });
    }

    return result;
  }, [services]);

  const otherServices = useMemo(() => {
    if (!services.length) {
      return [];
    }

    const criticalNames = new Set(
      criticalServices.map((service) =>
        String(service.name).toLowerCase()
      )
    );

    return services
      .map((service) => ({
        ...service,
        label:
          service.label && String(service.label).trim()
            ? service.label
            : serviceLabelFromValue(service.value),
      }))
      .filter(
        (service) =>
          !criticalNames.has(
            String(service.name).toLowerCase()
          )
      )
      .sort((a, b) =>
        String(a.name).localeCompare(String(b.name))
      );
  }, [services, criticalServices]);

  return (
    <div className="card pc-saas">
      {/* =====================================================
          CABECERA DEL EQUIPO
      ====================================================== */}

      <div className="pc-saas-head">
        <div className="pc-saas-title">
          <div className="pc-saas-h1">
            {pc?.descripcion ||
              pc?.nombre ||
              `Equipo ${pc?.id}`}

            <span className="chip chip-muted">
              ID #{pc?.id}
            </span>

            {pfSense ? (
              <span
                className="badge badge-info"
                title="Equipo detectado como pfSense"
              >
                pfSense
              </span>
            ) : null}

            {agentOk === null ? (
              <span className="badge badge-muted">
                —
              </span>
            ) : agentOk ? (
              <span className="badge badge-ok">
                Agente OK
              </span>
            ) : (
              <span className="badge badge-bad">
                Agente KO
              </span>
            )}

            {incidenciasCount > 0 ? (
              <span className="badge badge-info">
                ⚠ {incidenciasCount}
              </span>
            ) : null}
          </div>

          <div className="pc-saas-sub">
            {pc?.hostname
              ? `🖥 ${pc.hostname}`
              : ""}

            {pc?.ip
              ? ` · 🌐 ${pc.ip}`
              : ""}

            {pc?.so
              ? ` · 💿 ${pc.so}`
              : ""}
          </div>

          {/* Información adicional del equipo */}
          {infoAdicional || editingInfo ? (
            <div className="pc-saas-info">
              <div className="pc-saas-info-head">
                <span className="pc-saas-info-label">Información del equipo</span>
                {!editingInfo && onUpdateInfo ? <button type="button" className="pc-info-edit" onClick={startEditingInfo}>Editar</button> : null}
              </div>
              {editingInfo ? (
                <div className="pc-info-editor">
                  <textarea autoFocus rows={3} maxLength={4000} value={infoDraft} onChange={(event) => setInfoDraft(event.target.value)} />
                  {infoError ? <span className="pc-info-error" role="alert">{infoError}</span> : null}
                  <div>
                    <button type="button" className="button secondary" disabled={savingInfo} onClick={() => setEditingInfo(false)}>Cancelar</button>
                    <button type="button" className="button primary" disabled={savingInfo} onClick={saveInfo}>{savingInfo ? "Guardando…" : "Guardar"}</button>
                  </div>
                </div>
              ) : <span className="pc-saas-info-text">{infoAdicional}</span>}
            </div>
          ) : null}
        </div>

        {/* Toolbar del equipo */}
        <div className="pc-saas-actions">
          {!infoAdicional && !editingInfo && onUpdateInfo ? (
            <button type="button" className="button secondary" onClick={startEditingInfo}>Añadir información</button>
          ) : null}
          <button
            type="button"
            className="button secondary"
            onClick={() => {
              if (onRefreshZabbix) {
                onRefreshZabbix();
              }
            }}
            disabled={
              !onRefreshZabbix || zabbixLoading
            }
            title="Actualizar datos de Zabbix de este equipo"
          >
            {zabbixLoading
              ? "Actualizando…"
              : "Actualizar Zabbix"}
          </button>

          {hasTv ? (
            <button
              type="button"
              className="button primary"
              onClick={() => openTeamViewer(tvId)}
              title="Abrir TeamViewer"
            >
              Conectar
            </button>
          ) : null}

          <button
            type="button"
            className="button secondary"
            onClick={() => setShowHistorial(true)}
            disabled={!historial.length}
            title={
              !historial.length
                ? "Sin historial"
                : "Ver historial"
            }
          >
            Historial
          </button>
        </div>
      </div>

      <nav className="pc-work-tabs" aria-label="Vista del mantenimiento">
        <button type="button" className={activeView === "work" ? "active" : ""} onClick={() => setActiveView("work")}>
          Parte de trabajo
        </button>
        <button type="button" className={activeView === "diagnostic" ? "active" : ""} onClick={() => setActiveView("diagnostic")}>
          Diagnóstico {incidenciasCount > 0 ? `(${incidenciasCount})` : ""}
        </button>
      </nav>

      {/* =====================================================
          MONITORIZACIÓN
      ====================================================== */}

      {activeView === "diagnostic" ? (
      <div className="pc-section">
        <div className="pc-section-head">
          <div className="pc-section-title">
            📡 Monitorización
          </div>

          <div className="pc-section-right">
            {zHostName ? (
              <span className="chip chip-muted">
                Host: {zHostName}
              </span>
            ) : null}

            <button
              type="button"
              className="button secondary"
              onClick={() =>
                setShowZabbixMore((current) => !current)
              }
              disabled={!z}
              title={
                !z
                  ? "No hay datos de Zabbix"
                  : ""
              }
            >
              {showZabbixMore
                ? "Ver menos"
                : "Ver más"}
            </button>
          </div>
        </div>

        {!z ? (
          <div className="muted">
            No hay datos de Zabbix para este equipo.
          </div>
        ) : (
          <>
            <div className="zbx-kpis">
              <div className="zbx-box">
                <div className="zbx-label">
                  CPU
                </div>

                <div className="zbx-value">
                  {pct(z.cpu_pct)}
                </div>
              </div>

              <div className="zbx-box">
                <div className="zbx-label">
                  RAM
                </div>

                <div className="zbx-value">
                  {pfSense
                    ? pct(z.ram_pct)
                    : ramLabel(z)}
                </div>
              </div>

              <div className="zbx-box">
                <div className="zbx-label">
                  Uptime
                </div>

                <div className="zbx-value">
                  {fmtUptime(z.uptime_s)}
                </div>
              </div>

              {!pfSense ? (
                <div className="zbx-box">
                  <div className="zbx-label">
                    Disco peor
                  </div>

                  <div className="zbx-value">
                    {worstDisk
                      ? `${worstDisk.drive}: ${pct(
                          worstDisk.used_pct
                        )}`
                      : "—"}
                  </div>
                </div>
              ) : null}

              <div className="zbx-box wide">
                <div className="zbx-label">
                  SO
                </div>

                <div className="zbx-value small">
                  {z.os || "—"}
                </div>
              </div>

              <div className="zbx-box wide">
                <div className="zbx-label">
                  Actualizado
                </div>

                <div className="zbx-value small">
                  {fmtClock(z.lastclock)}
                </div>
              </div>
            </div>

            {!pfSense &&
            criticalServices.length > 0 ? (
              <div className="zbx-critical">
                <div className="section-label">
                  Servicios críticos
                </div>

                <div className="chips-row">
                  {criticalServices.map((service) => {
                    const pill = servicePill(
                      service.label
                    );

                    return (
                      <span
                        key={service.name}
                        className={pill.cls}
                        title={`${service.title} · ${service.name} | state=${service.value} | ${service.label}`}
                      >
                        {pill.icon} {service.title}:{" "}
                        {pill.text}
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {showZabbixMore ? (
              <div className="zbx-more">
                {/* Interfaces pfSense */}
                {pfSense ? (
                  <div style={{ marginTop: 12 }}>
                    <div className="section-label">
                      Interfaces (pfSense)
                    </div>

                    {pfIfaces.length === 0 ? (
                      <div className="muted">
                        —
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(220px, 1fr))",
                          gap: 10,
                        }}
                      >
                        {pfIfaces.map((iface) => {
                          const summary =
                            summarizeInterface(iface);

                          const warn =
                            summary.hasWarn;

                          return (
                            <div
                              key={iface.ifname}
                              className="zbx-box"
                              style={{
                                padding: 12,
                                border: warn
                                  ? "1px solid rgba(255, 180, 0, .35)"
                                  : undefined,
                              }}
                              title={`${iface.ifname} | in=${iface.in_bps} bps | out=${iface.out_bps} bps | err=${summary.errors} | drop=${summary.drops}`}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent:
                                    "space-between",
                                  gap: 10,
                                }}
                              >
                                <div
                                  style={{
                                    fontWeight: 700,
                                  }}
                                >
                                  {iface.ifname}{" "}
                                  <span
                                    style={{
                                      opacity: 0.85,
                                    }}
                                  >
                                    🟢
                                  </span>
                                </div>

                                {warn ? (
                                  <span className="badge badge-warn">
                                    ⚠️
                                  </span>
                                ) : (
                                  <span className="badge badge-ok">
                                    OK
                                  </span>
                                )}
                              </div>

                              <div
                                style={{
                                  marginTop: 8,
                                  display: "flex",
                                  gap: 10,
                                  flexWrap: "wrap",
                                }}
                              >
                                <span className="chip chip-muted">
                                  ↓{" "}
                                  {summary.inMbps == null
                                    ? "—"
                                    : `${summary.inMbps} Mbps`}
                                </span>

                                <span className="chip chip-muted">
                                  ↑{" "}
                                  {summary.outMbps == null
                                    ? "—"
                                    : `${summary.outMbps} Mbps`}
                                </span>
                              </div>

                              {summary.errors > 0 ||
                              summary.drops > 0 ? (
                                <div
                                  style={{
                                    marginTop: 8,
                                    display: "flex",
                                    gap: 10,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {summary.errors > 0 ? (
                                    <span className="badge badge-warn">
                                      Errors:{" "}
                                      {summary.errors}
                                    </span>
                                  ) : null}

                                  {summary.drops > 0 ? (
                                    <span className="badge badge-warn">
                                      Drops:{" "}
                                      {summary.drops}
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Otros servicios */}
                {!pfSense &&
                otherServices.length > 0 ? (
                  <div style={{ marginTop: 12 }}>
                    <div className="section-label">
                      Más servicios
                    </div>

                    <div className="chips-row">
                      {otherServices
                        .slice(0, 50)
                        .map((service) => {
                          const pill = servicePill(
                            service.label
                          );

                          return (
                            <span
                              key={service.name}
                              className={pill.cls}
                              title={`${service.name} | state=${service.value} | ${service.label}`}
                            >
                              {pill.icon}{" "}
                              {service.name}:{" "}
                              {pill.text}
                            </span>
                          );
                        })}

                      {otherServices.length > 50 ? (
                        <span
                          className="badge badge-muted"
                          title="Recortado para evitar ruido"
                        >
                          +
                          {otherServices.length - 50}{" "}
                          más…
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {/* Discos */}
                {!pfSense ? (
                  <div style={{ marginTop: 12 }}>
                    <div className="section-label">
                      Discos
                    </div>

                    {disks.length === 0 ? (
                      <div className="muted">
                        —
                      </div>
                    ) : (
                      <div className="disk-list">
                        {disks.map((disk) => {
                          const used = Number(
                            disk.used_pct
                          );

                          const warn =
                            Number.isFinite(used) &&
                            used >= 90;

                          return (
                            <div
                              key={disk.drive}
                              className="disk-item"
                            >
                              <div className="disk-top">
                                <div className="disk-drive">
                                  {disk.drive}{" "}
                                  {warn ? "⚠️" : ""}
                                </div>

                                <div className="disk-sub">
                                  {gb(disk.free_gb)}{" "}
                                  libres /{" "}
                                  {gb(disk.total_gb)}
                                </div>
                              </div>

                              <div className="disk-bar">
                                <div
                                  className={`disk-bar-fill ${
                                    warn ? "warn" : ""
                                  }`}
                                  style={{
                                    width: `${barWidthPct(
                                      disk.used_pct
                                    )}%`,
                                  }}
                                />
                              </div>

                              <div className="disk-pct">
                                {pct(disk.used_pct)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Problemas activos */}
                {problems.length > 0 ? (
                  <div style={{ marginTop: 12 }}>
                    <div className="section-label">
                      Problemas activos
                    </div>

                    <ul className="zbx-problems">
                      {problems
                        .slice(0, 10)
                        .map((problem, index) => (
                          <li
                            key={
                              problem.eventid ||
                              `${problem.name}-${index}`
                            }
                          >
                            {problem.name}
                          </li>
                        ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
      ) : null}

      {/* =====================================================
          ÚLTIMO MANTENIMIENTO
      ====================================================== */}

      {activeView === "work" ? (
      <>
      <div className="pc-last-summary">
        <div className="pc-section-head">
          <div className="pc-last-summary-main">
            <div className="pc-section-title">Último mantenimiento</div>
            <div className="pc-last-summary-text">
              {ultimo
                ? [ultimo.fecha, ultimo.tecnico, ultimoObs || ultimoInc].filter(Boolean).join(" · ")
                : "No hay mantenimientos previos registrados."}
            </div>
          </div>
          {ultimo ? <button type="button" className="button secondary" onClick={() => setShowLastMaintenance((value) => !value)}>{showLastMaintenance ? "Ocultar" : "Ver detalle"}</button> : null}
        </div>

        {ultimo && showLastMaintenance ? (
          <div className="pc-last">
            <div className="pc-last-row">
              <div className="pc-last-label">
                Observaciones
              </div>

              <div
                className={`pc-last-text ${
                  !ultimoObs ? "muted" : ""
                }`}
              >
                {ultimoObs || "—"}
              </div>
            </div>

            <div className="pc-last-row">
              <div className="pc-last-label">
                Incidencias
              </div>

              <div
                className={`pc-last-text ${
                  !ultimoInc ? "muted" : ""
                }`}
              >
                {ultimoInc || "—"}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* =====================================================
          PARTE DE MANTENIMIENTO
      ====================================================== */}

      <div className="pc-section">
        <div className="pc-section-head">
          <div className="pc-section-title">
            📝 Parte de mantenimiento
          </div>
        </div>

        <div className="pc-form-grid">
          <div>
            <label htmlFor={`fecha-${pc?.id}`}>
              Fecha
            </label>

            <input
              id={`fecha-${pc?.id}`}
              type="date"
              value={form?.fecha || ""}
              onChange={(event) =>
                changeForm(
                  "fecha",
                  event.target.value
                )
              }
            />
          </div>

          <div>
            <label htmlFor={`estado-${pc?.id}`}>
              Estado
            </label>

            <select
              id={`estado-${pc?.id}`}
              value={selectedStatus}
              onChange={(event) =>
                changeForm(
                  "status",
                  event.target.value
                )
              }
            >
              <option value="completed">✅ Completado</option>
              <option value="partial">🟡 Parcial</option>
              <option value="blocked">⛔ Bloqueado</option>
              <option value="follow_up">📅 Requiere seguimiento</option>
              <option value="not_completed">❌ No realizado</option>
            </select>
          </div>

          <div>
            <label htmlFor={`tecnico-${pc?.id}`}>
              Técnico
            </label>

            <input
              id={`tecnico-${pc?.id}`}
              type="text"
              value={form?.tecnico || ""}
              onChange={(event) =>
                changeForm(
                  "tecnico",
                  event.target.value
                )
              }
            />
          </div>
          {selectedStatus === "follow_up" ? (
            <div>
              <label htmlFor={`seguimiento-${pc?.id}`}>Próxima actuación</label>
              <input id={`seguimiento-${pc?.id}`} type="date" min={form?.fecha || undefined} value={form?.follow_up_date || ""} onChange={(event) => changeForm("follow_up_date", event.target.value)} />
            </div>
          ) : null}
        </div>

        {selectedStatus !== "completed" ? (
          <div className="pc-status-guidance">Este estado no cerrará el mantenimiento mensual. Indica el motivo o trabajo pendiente en observaciones o incidencias.</div>
        ) : null}

        <div className="pc-textareas">
          <div>
            <label
              htmlFor={`observaciones-${pc?.id}`}
            >
              Observaciones
            </label>

            <textarea
              id={`observaciones-${pc?.id}`}
              rows={3}
              value={form?.observaciones || ""}
              onChange={(event) =>
                changeForm(
                  "observaciones",
                  event.target.value
                )
              }
            />
          </div>

          <div>
            <label
              htmlFor={`incidencias-${pc?.id}`}
            >
              Incidencias detectadas
            </label>

            <textarea
              id={`incidencias-${pc?.id}`}
              rows={3}
              value={form?.incidencias || ""}
              onChange={(event) =>
                changeForm(
                  "incidencias",
                  event.target.value
                )
              }
            />
          </div>
        </div>
      </div>

      {/* =====================================================
          ACCIONES
      ====================================================== */}

      <div className="pc-footer">
        {saveStatus.message ? <span className={`pc-save-status ${saveStatus.state}`} role="status">{saveStatus.message}</span> : null}
        <button
          type="button"
          className="button secondary"
          disabled={saveStatus.state === "saving"}
          onClick={() => saveMaintenance(true)}
        >
          Guardar y siguiente
        </button>
        <button
          type="button"
          className="button primary"
          disabled={saveStatus.state === "saving"}
          onClick={() => saveMaintenance(false)}
        >
          {saveStatus.state === "saving" ? "Guardando…" : "Guardar mantenimiento"}
        </button>
      </div>
      </>
      ) : null}

      {/* =====================================================
          MODAL DEL HISTORIAL
      ====================================================== */}

      {showHistorial ? (
        <div
          className="modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowHistorial(false);
            }
          }}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pc-historial-title"
          >
            <div className="modal-header">
              <h3 id="pc-historial-title">
                Historial de mantenimientos
              </h3>
            </div>

            <div className="modal-body">
              {historial.length === 0 ? (
                <p>
                  No hay mantenimientos registrados.
                </p>
              ) : (
                <table className="tabla-historial">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Técnico</th>
                      <th>Realizado</th>
                      <th>Observaciones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {historial.map((mantenimiento) => (
                      <tr key={mantenimiento.id}>
                        <td>
                          {mantenimiento.fecha}
                        </td>

                        <td>
                          {mantenimiento.tecnico ||
                            "—"}
                        </td>

                        <td>
                          {maintenanceStatus(mantenimiento).icon}{" "}
                          {maintenanceStatus(mantenimiento).label}
                        </td>

                        <td>
                          {mantenimiento.observaciones ||
                            "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="button secondary"
                onClick={() =>
                  setShowHistorial(false)
                }
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
